import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ECOMMERCES, Shop } from '@app/services/shops/shop.model';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { FieldType, ProductsSyncRequest, SettingsService } from '../../../services/settings/settings.service';
import {
  ProductsSyncProgressUpdate,
  WebsocketSubscriberService,
} from '@app/core/services/websocket-subscriber/websocket.subscriber.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from '@app/core/services/api/api.service';
import { getApiUrl, getFrontEndApi, getShopLogoUrl } from '@app/core/services/utils/util.service';

import { accountFields, AccountField } from './models';
import moment from 'moment';

import { CountryCode, getCountryCodeByCountryName } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { ShopService } from '@app/services/shops/shop.service';
import { MatDialog } from '@angular/material/dialog';
import { ResetStoreDialogComponent } from './reset-store-dialog/reset-store-dialog.component';
import { SyncProductsDialogComponent } from './sync-products-dialog/sync-products-dialog.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-account-settings-page',
  templateUrl: './account-page.component.html',
  styleUrls: ['./account-page.component.scss', '../settings.scss'],
  standalone: false,
})
export class AccountPageComponent implements OnDestroy {
  @ViewChild('additionalSubFormFields') additionalSubFormFields: TemplateRef<any>;
  readonly FieldType = FieldType;
  fieldsSettings: AccountField[] = accountFields;
  isLoading = true;
  formGroup: UntypedFormGroup;
  initialForm: UntypedFormGroup;
  shop: Shop = null;
  shopCountryCode: CountryCode | null = null;
  shopPlatform: ECOMMERCES | null = null;
  shopMetadata: any = null;
  logoFile: string;
  defaultLogoSrc: string;
  notification = { duration: 'short', msg: '', type: 'info', state: 'out' };
  syncMinutesDuration = 59;
  lastSyncTime: any;
  isAdmin = null;
  isShopifyShop = false;
  resetStoreCompleted = true;

  timeUnits = [
    {
      label: 'AM',
      value: 'AM',
    },
    {
      label: 'PM',
      value: 'PM',
    },
  ];
  loadShop$: Observable<Shop>;
  loadShopSubscription$: Subscription;
  editComplete$: Observable<any>;
  editCompleteSubscription$: Subscription;
  syncRpoducts$: Observable<any>;
  syncRpoductsSubscription$: Subscription;
  syncDetails$: Observable<any>;
  syncDetailsSubscription$: Subscription;
  readonly productsSyncedCount = signal<number | null>(null);
  readonly isProductsSyncing = computed(() => this.productsSyncedCount() !== null);

  private destroyRef = inject(DestroyRef);
  private toastService = inject(ToastService);
  private websocketSubscriber = inject(WebsocketSubscriberService);

  constructor(
    private settingsSrv: SettingsService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    private shopService: ShopService,
    private dialog: MatDialog,
  ) {
    this.loadShopDetails();
  }

  ngOnDestroy(): void {
    this.loadShopSubscription$?.unsubscribe();
    this.editCompleteSubscription$?.unsubscribe();
    this.syncDetailsSubscription$?.unsubscribe();
    this.syncRpoductsSubscription$?.unsubscribe();
  }

  loadShopDetails() {
    this.isAdmin = this.apiService.isAdmin();
    const shopId = this.shopService.shopId;
    this.loadShop$ = this.settingsSrv.loadShopByID(shopId);
    this.loadShopSubscription$ = this.loadShop$.subscribe((shop) => {
      if (!shop) {
        this.notify('fail', 'Save changes failed');
        return;
      }
      this.shop = shop;
      this.shopPlatform = shop?.platform as ECOMMERCES;
      this.shopMetadata = shop?.metadata;
      this.isShopifyShop = !shop.platform || shop.platform === ECOMMERCES.SHOPIFY;
      if (this.isShopifyShop) {
        this.trackProductsSyncProgress();
      }
      this.shopCountryCode = shop.address?.country
        ? getCountryCodeByCountryName(shop.address?.country)
        : shop.address?.countryCode
          ? getCountryCodeByCountryName(shop.address?.countryCode)
          : null;
      this.logoFile = getShopLogoUrl(shop.id);
      this.defaultLogoSrc = `${getFrontEndApi()}assets/images/shop-img.svg`;
      this.initForm();
    });
  }

  initForm() {
    let formFields = {};
    const displayedFields = accountFields.filter(
      (f) => !f.displayCondition || f.displayCondition(this.shopCountryCode, this.shopPlatform, this.shopMetadata),
    );
    this.fieldsSettings = displayedFields;
    this.fieldsSettings
      .filter((f) => !f.generateSubForm)
      .filter((f) => f.type === FieldType.INPUT || f.type === FieldType.TOGGLE || f.type === FieldType.INPUT_ARROW)
      .map((f) => {
        let val = this.getObjValueByPath(this.shop, f.dataName);
        val = typeof val !== 'undefined' ? val : typeof f.default !== 'undefined' ? f.default : '';
        const formattedVal = val ? (f.formatter ? f.formatter(val) : val) : '';
        const validators = f.validators || [];
        formFields[f.name] = [formattedVal, validators];
      });
    formFields['defaultDeliveryPromise'][1].push(Validators.pattern('^\\d+(\\.\\d+)*$'));

    this.fieldsSettings
      .filter((f) => f.generateSubForm && f.applySubForm)
      .forEach((f) => {
        const fieldValue = this.getObjValueByPath(this.shop, f.dataName);
        const subForm = f.generateSubForm(fieldValue, this.shop);
        formFields = { ...subForm, ...formFields };
      });

    //formFields['title'][1].push(Validators.minLength(2));

    this.formGroup = this.formBuilder.group(formFields);

    this.fieldsSettings
      .filter((f) => f.type === FieldType.FILE)
      .map((f) =>
        this.formGroup.addControl(f.name, this.formBuilder.group({ [f.name]: this.logoFile, disabled: !f.editable })),
      );

    this.initialForm = this.formGroup.value;

    this.isLoading = false;
  }

  resetForm() {
    this.formGroup.setValue({ ...this.initialForm });
    this.clearLogo();
  }

  setInitialValues(values) {
    this.initialForm = values;
    this.formGroup.markAsPristine();
  }

  syncShopDetails() {
    this.syncDetailsSubscription$ = this.settingsSrv.syncShop().subscribe(
      (response) => {
        if (!response) {
          this.notify('fail', 'Sync failed');
          return;
        }

        this.shop.metadata.locale = response.locale;
        this.shop.metadata.currency = response.currency;
        this.shop.metadata.ianaTimeZone = response.ianaTimeZone;

        this.notify('info', 'Sync completed');

        this.formGroup.controls['currency'].patchValue(this.shop.metadata.currency);
        this.formGroup.controls['locale'].patchValue(this.shop.metadata.locale);
        this.formGroup.controls['ianaTimeZone'].patchValue(this.shop.metadata.ianaTimeZone);

        this.initialForm['currency'] = this.shop.metadata.currency;
        this.initialForm['locale'] = this.shop.metadata.locale;
        this.initialForm['ianaTimeZone'] = this.shop.metadata.ianaTimeZone;
      },
      (err) => {
        this.notify('fail', 'Sync failed');
      },
    );
  }

  syncProducts() {
    this.lastSyncTime = moment();
    const subscriber$ = this.settingsSrv.syncProducts(this.shop).subscribe(
      (response) => {
        console.log(response);
      },
      (err) => {
        const lastSyncTimeMs = err.error?.existsSyncRequest;
        this.lastSyncTime = moment(lastSyncTimeMs);
      },
    );
  }

  openSyncProductsDialog() {
    this.dialog
      .open(SyncProductsDialogComponent, { width: '600px', panelClass: 'overflow-visible-class' })
      .afterClosed()
      .pipe(
        filter((productsSyncRequest: ProductsSyncRequest | undefined) => !!productsSyncRequest),
        switchMap((productsSyncRequest: ProductsSyncRequest) =>
          this.settingsSrv.syncShopifyProducts(productsSyncRequest),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.productsSyncedCount.update((productsSyncedCount) => productsSyncedCount ?? 0),
        error: () => this.toastService.showToast({ message: 'Sync products failed', mode: 'danger' }),
      });
  }

  cancelProductsSync() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Stop products sync?',
          description: 'Products that were already synced will keep their updates.',
          confirmText: 'Stop sync',
          cancelText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        filter((isConfirmed: boolean) => isConfirmed),
        switchMap(() => this.settingsSrv.cancelShopifyProductsSync()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.productsSyncedCount.set(null);
          this.toastService.showToast({ message: 'Products sync stopped', mode: 'success' });
        },
        error: () => this.toastService.showToast({ message: 'Failed to stop products sync', mode: 'danger' }),
      });
  }

  resetDemoShop() {
    const dialogRef = this.dialog.open(ResetStoreDialogComponent, {
      width: '712px',
      height: '352px',
      panelClass: 'overflow-visible-class',
      data: {
        storeName: this.shop.title,
      },
    });
    // this.removeSubscriptions();
    dialogRef.afterClosed().subscribe((res) => {
      if (res.validOrders) {
        this.resetStoreCompleted = false;
        const subscriber$ = this.settingsSrv
          .resetDemoShop({ verifiedOrders: res.verifiedOrders, assignedOrders: res.assignedOrders })
          .subscribe(
            (response) => {
              this.resetStoreCompleted = true;
              this.notify('info', 'Reset shop completed', 10000);
            },
            (err) => {
              this.resetStoreCompleted = true;
              this.notify('fail', 'Reset shop failed', 10000);
            },
          );
      }
    });
  }

  clearLogo() {
    const initialLogoFile = this.initialForm['logo']?.logo;
    this.formGroup.controls['logo'].patchValue({ logo: initialLogoFile });
    this.changeDetectorRef.detectChanges();
  }

  onSubmitLogo() {
    const logoControl = this.formGroup.controls['logo'] as UntypedFormGroup;
    if (!logoControl?.dirty) {
      return;
    }

    const file = logoControl.controls['logo'].value?.[0] ?? logoControl.controls['logo'].value;
    if (!file || typeof file === 'string') {
      return;
    }

    this.settingsSrv.uploadLogo(file).subscribe({
      next: () => {
        this.logoFile = `${getShopLogoUrl(this.shop.id)}?t=${Date.now()}`;
        this.clearLogo();
      },
      error: () => {
        this.notify('fail', 'Failed to upload logo');
      },
    });
  }

  onSubmit(): void {
    this.onSubmitLogo();

    if (this.formGroup.valid && this.formGroup.dirty) {
      const values = this.formGroup.value;
      const editableValues = [];

      this.fieldsSettings
        .filter((f) => f.generateSubForm && f.applySubForm)
        .forEach((f) => {
          const finalValue = f.applySubForm(this.formGroup);
          if (finalValue) {
            editableValues.push(...finalValue);
          }
        });

      this.fieldsSettings
        .filter((f) => !f.applySubForm)
        .map((f) => {
          const control = this.formGroup.get(f.name);
          if (f.editable && control.dirty) {
            if (f.dataName.includes(',')) {
              const dataNames = f.dataName.split(',');
              dataNames.map((dataName) => {
                editableValues.push({ path: dataName, value: values[f.name] });
              });
            } else {
              editableValues.push({ path: f.dataName, value: values[f.name] });
            }
          }
        });

      this.editComplete$ = this.settingsSrv.editShop(editableValues);
      this.editCompleteSubscription$ = this.editComplete$.subscribe(
        (res) => {
          this.notify('info', 'Saved');
          this.setInitialValues(values);
          this.shopService.refreshShop();
        },
        (err) => {
          this.notify('fail', 'Failed to save settings');
        },
      );
    }
  }

  /* UI getters */

  get footerStateClasses() {
    return !this.formGroup.dirty ? ['initial'] : this.isChanged ? ['apear'] : ['disapear'];
  }

  get fieldsAreaClasses() {
    return this.footerStateClasses.includes('apear') ? ['footerBottomPadding'] : [''];
  }

  get uploadLogoUrl() {
    return getApiUrl() + 'shops/upload-logo';
  }

  get changesCount() {
    if (!this.formGroup || !this.initialForm) return;
    return Object.entries(this.formGroup?.controls).filter((value) => {
      if (value[1] instanceof UntypedFormControl) return value[1].value != this.initialForm[value[0]];
      if (value[1] instanceof UntypedFormGroup) {
        let isSomeKeyChanged = false;
        if (this.initialForm[value[0]]) {
          const initialFormKeys: string[] = Object.keys(this.initialForm[value[0]]);

          for (const formKey of initialFormKeys) {
            if (value[1].value[formKey] !== this.initialForm[value[0]][formKey]) {
              isSomeKeyChanged = true;
              break;
            }
          }
        }

        return (
          (value[1].value[value[0]] != this.initialForm[value[0]][value[0]] &&
            value[1].value[value[0]] != this.initialForm[value[0]]) ||
          isSomeKeyChanged
        );
      }
    }).length;
  }
  get changedStatusTxt() {
    if (!this.isChanged) return '';
    return ` You've changed ${this.changesCount} setting `;
  }
  get isChanged() {
    return this.formGroup.dirty && this.changesCount > 0;
  }
  get isValid() {
    return this.formGroup.valid;
  }
  isControlValid(controlName) {
    return this.formGroup.controls[controlName].valid;
  }
  get syncRemainingTime() {
    return this.syncMinutesDuration - moment().diff(this.lastSyncTime, 'minute');
  }
  get isAllowedSyncProducts() {
    return !this.lastSyncTime || this.syncMinutesDuration <= moment().diff(this.lastSyncTime, 'minute');
  }

  get isEnabledSyncShop() {
    return true;
  }

  /* private methods */
  getObjValueByPath(obj: object, path: string) {
    if (path.includes(',')) {
      const pathes = path.split(',');
      return pathes.every((path) => {
        const val = path.split('.').reduce((val, currPath) => (val ? val[currPath] : ''), obj);
        return val;
      });
    }
    const val = path.split('.').reduce((val, currPath) => (val ? val[currPath] : ''), obj);

    return val;
  }

  clearCutoff() {
    const cutoffForm = this.formGroup.controls.pickupCutoffTime as UntypedFormGroup;

    cutoffForm.controls.hours.setValue(null);
    cutoffForm.controls.minutes.setValue(null);
    cutoffForm.markAsDirty();
  }
  notify(type: string, msg: string, ttl?: number) {
    this.notification.duration = 'short';
    this.notification.msg = msg;
    this.notification.type = type;
    this.notification.state = 'in';

    setTimeout(() => (this.notification.state = 'out'), ttl ?? 5000);
  }

  private trackProductsSyncProgress() {
    const productsSyncProgressUpdate$ = this.websocketSubscriber.productsSyncProgress$;

    this.settingsSrv
      .getProductsSyncProgress()
      .pipe(
        catchError(() => of(null)),
        takeUntil(productsSyncProgressUpdate$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((productsSyncProgress) => this.productsSyncedCount.set(productsSyncProgress?.progress ?? null));

    productsSyncProgressUpdate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((productsSyncProgressUpdate) => this.applyProductsSyncProgressUpdate(productsSyncProgressUpdate));
  }

  private applyProductsSyncProgressUpdate({ status, progress }: ProductsSyncProgressUpdate) {
    if (status === 'in_progress') {
      this.productsSyncedCount.set(progress);
      return;
    }

    this.productsSyncedCount.set(null);
    if (status === 'failed') {
      this.toastService.showToast({ message: 'Products sync failed', mode: 'danger' });
      return;
    }
    this.toastService.showToast({
      message: `Products sync completed, ${progress.toLocaleString()} products synced`,
      mode: 'info',
    });
  }
}
